import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Check, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { usePortalProfile } from "../use-profile";
import { useUpdatePortalProfile } from "../use-update-profile";
import { useCurrentPartner } from "../use-current-partner";

// Phase 7 sub-bucket 7 — partner-side profile page.
//
// Replaces /app/portal/profile/page.tsx + ProfileForm.tsx. Deliberate
// divergence from the legacy submit-button form: each editable field
// auto-saves on blur via useUpdatePortalProfile (Pattern B field-level
// optimism, established in Phase 6 bucket 3). A small "Saved" pill flashes
// next to a field for ~1.5s after a successful save.
//
// Setup mode: when the auth callback routes a freshly-onboarded partner here
// with `?setup=1`, the header copy reads "Complete your profile" and the
// param is stripped from the URL on the first successful save so a returning
// user with a complete profile doesn't keep seeing the onboarding hint.

type FieldKey = "full_name" | "role_at_partner" | "website";

const REQUIRED_FIELDS: FieldKey[] = ["full_name", "role_at_partner"];

export function ProfilePage() {
  const [params, setParams] = useSearchParams();
  const isSetup = params.get("setup") === "1";

  const profileQuery = usePortalProfile();
  const partnerQuery = useCurrentPartner();
  const mutation = useUpdatePortalProfile();

  const [draft, setDraft] = useState<Record<FieldKey, string>>({
    full_name: "",
    role_at_partner: "",
    website: "",
  });
  // Tracks the original server-side value per field so on-blur we can decide
  // whether the field actually changed before firing a PATCH.
  const baselineRef = useRef<Record<FieldKey, string>>({
    full_name: "",
    role_at_partner: "",
    website: "",
  });
  // Per-field flash timestamp; non-null means a "Saved" pill is currently
  // visible. Set by the onSuccess of a field's PATCH; cleared by setTimeout.
  const [savedFlash, setSavedFlash] = useState<Record<FieldKey, boolean>>({
    full_name: false,
    role_at_partner: false,
    website: false,
  });
  const flashTimers = useRef<Record<FieldKey, ReturnType<typeof setTimeout> | null>>({
    full_name: null,
    role_at_partner: null,
    website: null,
  });
  // Tracks which field is currently mid-save so we can render a tiny spinner
  // next to the active field (rather than a global mutation pending bar).
  const [pendingField, setPendingField] = useState<FieldKey | null>(null);
  // Whether we've already stripped ?setup=1 — guards against re-stripping on
  // every save once the user is in edit mode.
  const setupStrippedRef = useRef(false);

  // I1 — PMS / systems this partner integrates with. Managed separately from
  // the text FieldKey draft because it's a tag list, not a string. Each
  // add/remove fires the same profile PATCH with the full patch payload.
  const [pmsTags, setPmsTags] = useState<string[]>([]);
  const [pmsInput, setPmsInput] = useState("");
  const [pmsSaving, setPmsSaving] = useState(false);
  const [pmsSaved, setPmsSaved] = useState(false);
  const pmsSavedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydrate local state from the server fetch when it first lands. Re-hydrate
  // on every successful invalidation pass too — the optimism layer keeps the
  // cache in sync, so the post-settle refetch should be a no-op for the
  // user's typed values.
  useEffect(() => {
    if (!profileQuery.data) return;
    const next = {
      full_name: profileQuery.data.full_name ?? "",
      role_at_partner: profileQuery.data.role_at_partner ?? "",
      website: profileQuery.data.website ?? "",
    };
    setDraft(next);
    baselineRef.current = next;
    setPmsTags(profileQuery.data.integrates_with ?? []);
  }, [profileQuery.data]);

  useEffect(() => {
    return () => {
      // Cleanup any in-flight flash timers on unmount.
      const timers = flashTimers.current;
      (Object.keys(timers) as FieldKey[]).forEach((k) => {
        const t = timers[k];
        if (t) clearTimeout(t);
      });
      if (pmsSavedTimer.current) clearTimeout(pmsSavedTimer.current);
    };
  }, []);

  const remainingRequired = useMemo(
    () => REQUIRED_FIELDS.filter((f) => !draft[f].trim()).length,
    [draft],
  );

  function handleBlur(field: FieldKey) {
    const value = draft[field].trim();
    const baseline = baselineRef.current[field];

    // No-op if unchanged.
    if (value === baseline) return;

    // Skip required-field empties — autosaving an empty required value would
    // 400 the worker. Field stays in error-by-omission state until the user
    // types again; we don't surface inline error chrome for empties.
    if (REQUIRED_FIELDS.includes(field) && value.length === 0) return;

    // Website validation: optional, but if present must look like a URL.
    if (field === "website" && value && !isLikelyUrl(value)) return;

    const patch = {
      full_name: draft.full_name.trim(),
      role_at_partner: draft.role_at_partner.trim(),
      website: draft.website.trim() === "" ? null : draft.website.trim(),
      integrates_with: pmsTags,
    };

    setPendingField(field);
    mutation.mutate(patch, {
      onSuccess: () => {
        baselineRef.current = {
          full_name: patch.full_name,
          role_at_partner: patch.role_at_partner,
          website: patch.website ?? "",
        };
        // Flash "Saved" next to the blurred field for ~1.5s.
        setSavedFlash((s) => ({ ...s, [field]: true }));
        const existingTimer = flashTimers.current[field];
        if (existingTimer) clearTimeout(existingTimer);
        flashTimers.current[field] = setTimeout(() => {
          setSavedFlash((s) => ({ ...s, [field]: false }));
        }, 1500);

        // First successful save in setup mode strips the URL param.
        if (isSetup && !setupStrippedRef.current) {
          setupStrippedRef.current = true;
          setParams({}, { replace: true });
        }
      },
      onSettled: () => {
        setPendingField((p) => (p === field ? null : p));
      },
    });
  }

  function savePmsTags(nextTags: string[]) {
    setPmsTags(nextTags);
    // The shared profile PATCH carries the required name/role fields too, so
    // only persist once those are present (a brand-new partner mid-setup edits
    // them first). Until then the tags stay local and save on the next change.
    if (!draft.full_name.trim() || !draft.role_at_partner.trim()) return;

    setPmsSaving(true);
    mutation.mutate(
      {
        full_name: draft.full_name.trim(),
        role_at_partner: draft.role_at_partner.trim(),
        website: draft.website.trim() === "" ? null : draft.website.trim(),
        integrates_with: nextTags,
      },
      {
        onSuccess: () => {
          setPmsSaved(true);
          if (pmsSavedTimer.current) clearTimeout(pmsSavedTimer.current);
          pmsSavedTimer.current = setTimeout(() => setPmsSaved(false), 1500);
        },
        onSettled: () => setPmsSaving(false),
      },
    );
  }

  function addPmsTag() {
    const value = pmsInput.trim();
    if (!value) return;
    if (pmsTags.some((t) => t.toLowerCase() === value.toLowerCase())) {
      setPmsInput("");
      return;
    }
    savePmsTags([...pmsTags, value]);
    setPmsInput("");
  }

  function removePmsTag(tag: string) {
    savePmsTags(pmsTags.filter((t) => t !== tag));
  }

  const email = profileQuery.data?.email ?? "";
  const company = partnerQuery.data?.company_name ?? "";

  return (
    <div className="max-w-xl">
      <header className="mb-7">
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          {isSetup ? "Complete your profile" : "Profile"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isSetup
            ? "Tell us a bit about yourself so the Bookboost team knows who they're working with."
            : "Update your details. Your email is your sign-in identity and can't be changed here."}
        </p>
        {isSetup && remainingRequired > 0 && (
          <p className="mt-2 text-xs font-medium text-muted-foreground">
            {remainingRequired === 1 ? "One field to go." : "Two fields to go."}
          </p>
        )}
      </header>

      {profileQuery.isLoading ? (
        <div className="grid gap-4">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" value={email} disabled className="bg-muted/40" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company">Company</Label>
              <Input id="company" value={company} disabled className="bg-muted/40" />
            </div>
          </div>

          <ProfileField
            id="full_name"
            label="Your name *"
            value={draft.full_name}
            required
            pending={pendingField === "full_name"}
            saved={savedFlash.full_name}
            onChange={(v) => setDraft((d) => ({ ...d, full_name: v }))}
            onBlur={() => handleBlur("full_name")}
          />

          <ProfileField
            id="role_at_partner"
            label="Your role *"
            value={draft.role_at_partner}
            required
            placeholder="VP Partnerships"
            pending={pendingField === "role_at_partner"}
            saved={savedFlash.role_at_partner}
            onChange={(v) => setDraft((d) => ({ ...d, role_at_partner: v }))}
            onBlur={() => handleBlur("role_at_partner")}
          />

          <ProfileField
            id="website"
            label="Company website"
            type="url"
            value={draft.website}
            placeholder="https://"
            pending={pendingField === "website"}
            saved={savedFlash.website}
            onChange={(v) => setDraft((d) => ({ ...d, website: v }))}
            onBlur={() => handleBlur("website")}
          />

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="integrates_with">
                Integrates with (PMS / systems)
              </Label>
              <SavedIndicator pending={pmsSaving} saved={pmsSaved} />
            </div>
            <p className="text-[0.8rem] text-muted-foreground">
              Which property-management systems or platforms do you connect to?
              Add each one (e.g. Mews, Apaleo, Cloudbeds).
            </p>
            {pmsTags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {pmsTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removePmsTag(tag)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${tag}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <Input
              id="integrates_with"
              value={pmsInput}
              placeholder="Type a system and press Enter"
              onChange={(e) => setPmsInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addPmsTag();
                }
              }}
              onBlur={addPmsTag}
            />
          </div>
        </div>
      )}
    </div>
  );
}

type ProfileFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  onBlur: () => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  pending: boolean;
  saved: boolean;
};

function ProfileField({
  id,
  label,
  value,
  onChange,
  onBlur,
  type,
  required,
  placeholder,
  pending,
  saved,
}: ProfileFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <SavedIndicator pending={pending} saved={saved} />
      </div>
      <Input
        id={id}
        name={id}
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    </div>
  );
}

function SavedIndicator({ pending, saved }: { pending: boolean; saved: boolean }) {
  if (pending) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs text-emerald-600 transition-opacity",
        saved ? "opacity-100" : "opacity-0",
      )}
      aria-live="polite"
    >
      <Check className="h-3 w-3" />
      Saved
    </span>
  );
}

function isLikelyUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
